// Eingabe. Zwei Schichten, weil das Konzept (§14) genau das als eigentlichen
// VR-Preis benennt: jede Aktion braucht eine Maus- und eine Controller-Variante.
// Deshalb fragt kein System direkt nach Tasten, sondern nach *Aktionen*.

const ACTION_KEYS = {
  forward:  ['KeyW', 'ArrowUp'],
  back:     ['KeyS', 'ArrowDown'],
  left:     ['KeyA', 'ArrowLeft'],
  right:    ['KeyD', 'ArrowRight'],
  run:      ['ShiftLeft', 'ShiftRight'],
  crouch:   ['ControlLeft', 'ControlRight', 'KeyC'],
  jump:     ['Space'],
  interact: ['KeyE'],
  paintTool:['KeyF'],
  radio:    ['KeyR'],
  tune:     ['KeyT'],
  photo:    ['KeyC'],
  // Persönliche Fotochronik (§4): reine Erinnerung, keine Karte/Statistik.
  logbook:  ['KeyB'],
  brushUp:  ['KeyW'],
  brushDown:['KeyQ'],
  debug:    ['F3'],
  // Dev: Godmode freiflug. G ist frei (nicht WASD/E/F/R/T/C/H) und merkwürdig.
  godMode:  ['KeyG'],
  // Taschenlampe. L = Lampe/Light, kollidiert mit keiner bestehenden Aktion.
  flashlight: ['KeyL'],
};

export class Input {
  constructor(canvas) {
    this.canvas = canvas;
    this.keys = new Set();
    this.pressed = new Set();   // in diesem Frame neu gedrückt
    this.released = new Set();
    this.mouse = { dx: 0, dy: 0, left: false, right: false, leftPressed: false, wheel: 0 };
    this.locked = false;
    this.textCapture = false;   // wenn ein Textfeld tippt, keine Spielsteuerung
    this.sensitivity = 0.0022;

    this._onKeyDown = (e) => {
      if (this.textCapture) {
        // Escape muss immer durchkommen, sonst sitzt man im Textfeld fest.
        if (e.code === 'Escape') this.pressed.add('Escape');
        return;
      }
      if (e.code === 'F3') e.preventDefault();
      if (e.code === 'Space') e.preventDefault();
      if (e.code === 'Tab') e.preventDefault();
      if (!this.keys.has(e.code)) this.pressed.add(e.code);
      this.keys.add(e.code);
    };
    this._onKeyUp = (e) => {
      this.keys.delete(e.code);
      this.released.add(e.code);
    };
    this._onMouseMove = (e) => {
      if (!this.locked) return;
      this.mouse.dx += e.movementX ?? 0;
      this.mouse.dy += e.movementY ?? 0;
    };
    this._onMouseDown = (e) => {
      if (this.textCapture) return;
      if (e.button === 0) { this.mouse.left = true; this.mouse.leftPressed = true; }
      if (e.button === 2) this.mouse.right = true;
    };
    this._onMouseUp = (e) => {
      if (e.button === 0) this.mouse.left = false;
      if (e.button === 2) this.mouse.right = false;
    };
    this._onWheel = (e) => { this.mouse.wheel += Math.sign(e.deltaY); };
    this._onContext = (e) => e.preventDefault();
    this._onLockChange = () => {
      this.locked = document.pointerLockElement === this.canvas;
    };
    this._onBlur = () => {
      // Fenster verlassen: alle Tasten loslassen, sonst rennt man ewig weiter.
      this.keys.clear();
      this.mouse.left = false;
      this.mouse.right = false;
    };

    window.addEventListener('keydown', this._onKeyDown);
    window.addEventListener('keyup', this._onKeyUp);
    window.addEventListener('mousemove', this._onMouseMove);
    window.addEventListener('mousedown', this._onMouseDown);
    window.addEventListener('mouseup', this._onMouseUp);
    window.addEventListener('wheel', this._onWheel, { passive: true });
    window.addEventListener('contextmenu', this._onContext);
    window.addEventListener('blur', this._onBlur);
    document.addEventListener('pointerlockchange', this._onLockChange);
  }

  requestLock() {
    if (!this.locked) this.canvas.requestPointerLock?.();
  }

  releaseLock() {
    if (this.locked) document.exitPointerLock?.();
  }

  down(action) {
    const codes = ACTION_KEYS[action];
    if (!codes) return false;
    for (const c of codes) if (this.keys.has(c)) return true;
    return false;
  }

  justPressed(action) {
    const codes = ACTION_KEYS[action];
    if (!codes) return false;
    for (const c of codes) if (this.pressed.has(c)) return true;
    return false;
  }

  key(code) { return this.keys.has(code); }
  keyPressed(code) { return this.pressed.has(code); }

  /** Bewegungsachsen als normierter Vektor (x = rechts, y = vorwärts). */
  moveAxis() {
    let x = 0, y = 0;
    if (this.down('forward')) y += 1;
    if (this.down('back')) y -= 1;
    if (this.down('right')) x += 1;
    if (this.down('left')) x -= 1;
    const len = Math.hypot(x, y);
    if (len > 1) { x /= len; y /= len; }
    return { x, y };
  }

  /** Mausdelta abholen und zurücksetzen. */
  takeLook() {
    const out = { dx: this.mouse.dx * this.sensitivity, dy: this.mouse.dy * this.sensitivity };
    this.mouse.dx = 0;
    this.mouse.dy = 0;
    return out;
  }

  /** Am Ende jedes Frames aufrufen. */
  endFrame() {
    this.pressed.clear();
    this.released.clear();
    this.mouse.leftPressed = false;
    this.mouse.wheel = 0;
  }

  dispose() {
    window.removeEventListener('keydown', this._onKeyDown);
    window.removeEventListener('keyup', this._onKeyUp);
    window.removeEventListener('mousemove', this._onMouseMove);
    window.removeEventListener('mousedown', this._onMouseDown);
    window.removeEventListener('mouseup', this._onMouseUp);
    window.removeEventListener('wheel', this._onWheel);
    window.removeEventListener('contextmenu', this._onContext);
    window.removeEventListener('blur', this._onBlur);
    document.removeEventListener('pointerlockchange', this._onLockChange);
  }
}

/** Muss als letztes System laufen: räumt die Frame-Flags auf. */
export class InputFlush {
  constructor(input) { this.input = input; }
  lateUpdate() { this.input.endFrame(); }
}
